/**
 * The van app's state: the phone's download, its documents, and the sync.
 *
 * Every document is saved to the phone first and only then sent. The
 * screen never waits for the network: billing in a basement shop is the
 * same speed as billing next to the depot.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { VanHttpError, fetchBootstrap, syncDocs } from './api'
import {
  applyBootstrap, docNo, loadDevice, loadDocs, nowUtc, replay, riyadhDate, saveDevice, saveDocs,
  type Bootstrap, type Device, type DocKind, type VanDoc,
} from './model'
import * as db from './db'

export interface VanState {
  device: Device | null | undefined          // undefined = still loading
  docs: VanDoc[]
  online: boolean
  syncing: boolean
  syncError: string | null
  /** The phone was switched off by the office, or its token expired. */
  locked: string | null
  persistent: boolean
  setUp: (token: string, b: Bootstrap) => Promise<void>
  save: (draft: Omit<VanDoc, 'uid' | 'doc_no' | 'issued_at' | 'doc_date' | 'sync'>) =>
    Promise<VanDoc>
  sync: (refresh?: boolean) => Promise<void>
  forget: () => Promise<void>
  /** A fresh download that arrived another way (after unloading). */
  applyDownload: (b: Bootstrap) => Promise<void>
}

export const VanCtx = createContext<VanState | null>(null)

export function useVanState(): VanState {
  const v = useContext(VanCtx)
  if (!v) throw new Error('useVanState outside VanCtx')
  return v
}

/** Documents kept on the phone once synced: today and the last few days. */
const KEEP_DAYS = 7

const unsynced = (docs: VanDoc[]) =>
  docs.filter((d) => d.sync?.status === 'QUEUED' || d.sync?.status === 'FAILED')

export function useVan(): VanState {
  const [device, setDevice] = useState<Device | null | undefined>(undefined)
  const [docs, setDocs] = useState<VanDoc[]>([])
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [locked, setLocked] = useState<string | null>(null)
  const [persistent, setPersistent] = useState(true)
  const busy = useRef(false)
  const latest = useRef<{ device: Device | null | undefined; docs: VanDoc[] }>({ device, docs })
  latest.current = { device, docs }

  useEffect(() => {
    let gone = false
    void (async () => {
      const [d, list, keep] = await Promise.all([loadDevice(), loadDocs(), db.persistent()])
      if (gone) return
      setDevice(d ?? null); setDocs(list); setPersistent(keep)
    })()
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => { gone = true; window.removeEventListener('online', up)
                   window.removeEventListener('offline', down) }
  }, [])

  const store = useCallback(async (d: Device | null, list: VanDoc[]) => {
    if (d) await saveDevice(d)
    await saveDocs(list)
    latest.current = { device: d, docs: list }
    setDevice(d); setDocs(list)
  }, [])

  const setUp = useCallback(async (token: string, b: Bootstrap) => {
    void db.askToKeep()
    await store(applyBootstrap(undefined, token, b), [])
    setLocked(null)
  }, [store])

  const sync = useCallback(async (refresh = false) => {
    const { device: d, docs: list } = latest.current
    if (!d || busy.current) return
    const queue = list.filter((x) => x.sync?.status === 'QUEUED')
    if (!queue.length && !refresh) return
    busy.current = true; setSyncing(true); setSyncError(null)
    try {
      let reply
      if (queue.length) {
        reply = await syncDocs(d.token, queue.slice(0, 300), refresh)
      } else {
        reply = { results: [], bootstrap: await fetchBootstrap(d.token), server_time: '' }
      }
      const byUid = new Map(reply.results.map((r) => [r.uid, r]))
      const at = new Date().toISOString()
      // Re-read: the salesman may have billed while the sync was in the air.
      const now = latest.current
      const merged = now.docs.map((x) => {
        const r = byUid.get(x.uid)
        return r ? { ...x, sync: { status: r.status, warnings: r.warnings,
                                   error: r.error ?? null, at } } : x
      })
      const cutoff = Date.now() - KEEP_DAYS * 86_400_000
      const kept = merged.filter((x) => unsynced([x]).length
        || new Date(x.issued_at).getTime() >= cutoff)
      const next = reply.bootstrap
        ? applyBootstrap(now.device ?? d, d.token, reply.bootstrap, unsynced(kept))
        : (now.device ?? d)
      await store(next, kept)
    } catch (e) {
      if (e instanceof VanHttpError && e.status === 401) setLocked(e.message)
      setSyncError(e instanceof Error ? e.message : String(e))
    } finally {
      busy.current = false; setSyncing(false)
    }
  }, [store])

  const save = useCallback(async (draft: Omit<VanDoc, 'uid' | 'doc_no' | 'issued_at'
    | 'doc_date' | 'sync'>) => {
    const d = latest.current.device
    if (!d) throw new Error('The phone is not set up')
    const now = new Date()
    const doc: VanDoc = {
      ...draft, uid: crypto.randomUUID(), doc_no: docNo(d, draft.kind as DocKind),
      doc_date: riyadhDate(now), issued_at: nowUtc(now), sync: { status: 'QUEUED' },
    }
    const next = replay({ ...d, next: { ...d.next, [doc.kind]: d.next[doc.kind] + 1 } }, [doc])
    await store(next, [doc, ...latest.current.docs])
    if (navigator.onLine) void sync()
    return doc
  }, [store, sync])

  const applyDownload = useCallback(async (b: Bootstrap) => {
    const { device: d, docs: list } = latest.current
    if (!d) return
    await store(applyBootstrap(d, d.token, b, unsynced(list)), list)
  }, [store])

  const forget = useCallback(async () => {
    await db.del('device'); await db.del('docs')
    setDevice(null); setDocs([]); setLocked(null)
  }, [])

  // Send the queue whenever the signal comes back, and every minute while
  // something is waiting.
  useEffect(() => {
    if (!device) return
    if (online) void sync()
    const t = window.setInterval(() => { if (navigator.onLine) void sync() }, 60_000)
    return () => window.clearInterval(t)
  }, [online, device?.token, sync])          // eslint-disable-line react-hooks/exhaustive-deps

  return { device, docs, online, syncing, syncError, locked, persistent, setUp, save, sync,
           forget, applyDownload }
}
